import { ActionBar, Label, Modal } from '@viapeople/components'
import { useState } from 'react'
import type { User, UserSearchInput } from '../../modules/users/types'
import { trpc } from '../../utils/trpc'
import { type Column, GenericTable } from '../GenericTable'

interface AddReviewersModalProps {
	isOpen: boolean
	excludedUserExternalIds: () => Set<string>
	onSelect: (users: User[]) => void
	onClose: () => void
	organizationId?: string
}

export const AddReviewersModal = ({
	isOpen,
	onClose,
	organizationId,
	excludedUserExternalIds,
	onSelect,
}: AddReviewersModalProps) => {
	const [selectedUsers, setSelectedUsers] = useState<User[]>([])

	const columns: Column<User & { customFields: Record<string, string> }>[] = [
		{
			field: 'firstName',
			label: 'Name',
			colSpan: 2,
			render: (user: User) => `${user.firstName} ${user.lastName}`,
		},
		{
			field: 'externalId',
			label: 'ID',
			colSpan: 1,
		},
		{
			field: 'email',
			label: 'Email',
			colSpan: 2,
		},
		{
			field: 'active',
			label: 'Status',
			render: (user: User) => (
				<div className='flex'>
					<Label className='self-start' color={user.active ? 'green' : 'default'}>
						{user.active ? 'Active' : 'Inactive'}
					</Label>
				</div>
			),
		},
	]

	return (
		<Modal isOpen={isOpen} fullContent onClose={onClose} fullScreen title='Add Users'>
			<div className='flex flex-col h-[calc(100vh-150px)]'>
				<GenericTable<User & { customFields: Record<string, string> }, UserSearchInput>
					queryOptionsGenerator={(options) => ({
						...trpc.admin.users.search.queryOptions({
							...options,
							organizationId: organizationId as string,
							excludingIds: Array.from(excludedUserExternalIds()),
							excludeCustomFields: true,
						}),
					})}
					isInternal
					selectable
					onSelectionChange={(users) => setSelectedUsers(users)}
					columns={columns}
				/>
			</div>

			<ActionBar
				variant='modal'
				className='left-0 flex justify-end'
				rightActions={[
					{
						label: 'Cancel',
						variant: 'secondary',
						onClick: (e) => {
							e.preventDefault()
							onClose()
						},
					},
					{
						label: 'Add users',
						variant: 'primary',
						onClick: (e) => {
							e.preventDefault()
							onSelect(selectedUsers)
							onClose()
						},
						disabled: selectedUsers.length === 0,
					},
				]}
			/>
		</Modal>
	)
}
